import { useEffect, useState } from "react"
import { Eye, Clock, DollarSign, Users, Sparkles, ArrowRight } from "lucide-react"
import { useDispatch, useSelector } from "react-redux"
import { isCatalogStore } from "../../utils/storeCategory"
import { allClientsData, allGeneralClientsData } from "../../store/actions"
import { getClientDetailsData, getGeneralClientDetails } from "../../api/products/get"
import ClientDetailsModal from "../Clients/ClientDetailsModal"
import { BASE_URL } from "../../api/api"

export default function Debtors() {
  const { userdata, allclientsdata, generalclientsdata } = useSelector((state: any) => state.UserReducer)
  const { store } = userdata
  const isGeneralStore = isCatalogStore(store)

  const [selectedClient, setSelectedClient] = useState<any | null>(null)
  const [clientDetails, setClientDetails] = useState<any | null>(null)
  const [page, setPage] = useState(1)

  const dispatch = useDispatch()

  const debtorsData = isGeneralStore ? generalclientsdata : allclientsdata
  const debtors: any = debtorsData?.data || []
  const stats = debtorsData?.stats || {
    totalClients: 0,
    totalDebt: 0,
    totalPurchases: 0,
  }
  const pagination = debtorsData?.pagination || {}
  const { totalPages } = pagination

  const storeId = store?._id
  const limit = 6
  const showDebtorsOnly = true

  const handleViewClient = async (client: any) => {
    try {
      setSelectedClient(client)

      const phone = client.phone || client.clientPhone || client?.client?.phone
      const data = isGeneralStore
        ? await getGeneralClientDetails(storeId, phone)
        : await getClientDetailsData(storeId, phone)
      setClientDetails(data)
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    if (!storeId) return
    if (isGeneralStore) {
      dispatch(allGeneralClientsData(storeId, page, limit, showDebtorsOnly))
    } else {
      dispatch(allClientsData(storeId, page, limit, showDebtorsOnly))
    }
  }, [storeId, page, showDebtorsOnly, isGeneralStore, dispatch])

  return (
    <>
      <div className="space-y-8">
        <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-sky-600 via-blue-700 to-blue-900 p-6 sm:p-7 text-white shadow-xl">
          <div className="pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-8 h-32 w-32 rounded-full bg-blue-200/20 blur-3xl" />

          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
                <Sparkles size={12} /> Recovery Desk
              </p>
              <h1 className="mt-3 text-2xl sm:text-3xl font-bold">Débiteurs</h1>
              <p className="mt-2 text-sm text-white/90 max-w-2xl">
                Suivi des clients avec paiements en attente et actions rapides d'encaissement.
              </p>
            </div>

            <div className="rounded-2xl border border-white/25 bg-white/10 px-4 py-3 backdrop-blur-sm">
              <p className="text-xs uppercase tracking-[0.12em] text-white/75">Clients à relancer</p>
              <p className="mt-1 text-xl font-semibold">{stats.totalClients || debtors.length}</p>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Kpi
            label="Clients débiteurs"
            value={stats.totalClients}
            icon={<Users size={18} />}
            color="from-rose-600 to-red-500"
            ring="ring-rose-100"
          />
          <Kpi
            label="Total des dettes"
            value={`${Number(stats.totalDebt || 0).toLocaleString()} FCFA`}
            icon={<DollarSign size={18} />}
            color="from-orange-600 to-amber-500"
            ring="ring-orange-100"
          />
          <Kpi
            label="Ventes concernées"
            value={stats.totalPurchases}
            icon={<Clock size={18} />}
            color="from-blue-600 to-indigo-600"
            ring="ring-blue-100"
          />
        </div>

        <div className="hidden md:block overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
            <p className="text-sm font-semibold text-slate-800">Liste des débiteurs</p>
            <p className="text-xs text-slate-500 mt-0.5">Priorisez les montants les plus élevés et ouvrez la fiche client en un clic.</p>
          </div>

          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="px-6 py-3">Client</th>
                <th className="px-6 py-3">Téléphone</th>
                <th className="px-6 py-3">Achats</th>
                <th className="px-6 py-3">Dette totale</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {debtors.map((c: any) => (
                <tr key={c.phone || c.clientPhone} className="border-t border-slate-100 hover:bg-slate-50/70 transition">
                  <td className="px-6 py-4">
                    <p className="font-medium text-slate-800">{c.name || c.clientName}</p>
                  </td>

                  <td className="px-6 py-4 text-slate-600">{c.phone || c.clientPhone}</td>

                  <td className="px-6 py-4">
                    <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                      {Number(c.purchases || 0)}
                    </span>
                  </td>

                  <td className="px-6 py-4">
                    <span className="inline-flex rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                      {Number(c.totalDebt || 0).toLocaleString()} FCFA
                    </span>
                  </td>

                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleViewClient(c)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 hover:bg-slate-100 transition"
                    >
                      <Eye size={14} /> Voir
                    </button>
                  </td>
                </tr>
              ))}

              {debtors.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-slate-500">
                    Aucun débiteur
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-4 md:hidden">
          {debtors.map((c: any) => (
            <div key={c.phone || c.clientPhone} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-4 py-3 flex justify-between items-start">
                <div>
                  <p className="font-semibold text-slate-800">{c.name || c.clientName}</p>
                  <p className="text-xs text-slate-500">{c.phone || c.clientPhone}</p>
                </div>
                <span className="text-xs bg-rose-100 text-rose-700 px-2 py-1 rounded-full">Dette</span>
              </div>

              <div className="p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Achats</span>
                  <span className="font-semibold text-slate-800">{Number(c.purchases || 0)}</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Dette totale</span>
                  <span className="text-rose-700 font-semibold">{Number(c.totalDebt || 0).toLocaleString()} FCFA</span>
                </div>

                <button
                  onClick={() => handleViewClient(c)}
                  className="w-full inline-flex items-center justify-center gap-1 rounded-xl border border-slate-200 py-2 text-slate-700 hover:bg-slate-50 transition"
                >
                  Voir / Encaisser <ArrowRight size={14} />
                </button>
              </div>
            </div>
          ))}

          {debtors.length === 0 && <div className="text-center text-slate-500 py-10">Aucun débiteur</div>}
        </div>

        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <p className="text-sm text-slate-500">
              Page <strong className="text-slate-700">{page}</strong> sur <strong className="text-slate-700">{totalPages}</strong>
            </p>

            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-4 py-2 rounded-xl border border-slate-200 disabled:opacity-50 hover:bg-slate-100 transition"
              >
                Précédent
              </button>

              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-4 py-2 rounded-xl bg-blue-600 text-white disabled:opacity-50 hover:bg-blue-700 transition"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedClient && (
        <ClientDetailsModal
          clientData={clientDetails}
          onClose={() => setSelectedClient(null)}
          onOpenSale={(sale) => {
            window.open(
              isGeneralStore
                ? `${BASE_URL}/api/gestock/general/invoice/${sale._id}/${storeId}`
                : `${BASE_URL}/api/gestock/invoice/${sale._id}/${storeId}`,
              "_blank"
            )
          }}
        />
      )}
    </>
  )
}

function Kpi({
  label,
  value,
  icon,
  color,
  ring,
}: {
  label: string
  value: string | number
  icon: React.ReactNode
  color: string
  ring: string
}) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ${ring}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-800 mt-0.5">{value}</p>
        </div>
        <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${color} text-white flex items-center justify-center shadow-lg`}>
          {icon}
        </div>
      </div>
    </div>
  )
}
